import React, { useState } from "react";
import { ArrowRightIcon, CheckIcon, ClockIcon } from "../membershipGuideIcons";
import { PLAN_OPTIONS } from "../membershipGuideConst";

const INITIAL_FORM = {
  name: "",
  phone: "",
  office: "",
  region: "",
  plan: "비즈니스",
  message: "",
};

const PHONE_ALLOWED_RE = /^[0-9\- ]+$/;

const validatePhone = (raw) => {
  const trimmed = raw.trim();
  if (!trimmed || !PHONE_ALLOWED_RE.test(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, "");
  return digits.length >= 9 && digits.length <= 11;
};

const validateForm = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "이름을 입력해주세요";
  if (!validatePhone(form.phone)) errors.phone = "연락처를 정확히 입력해주세요";
  if (!form.office.trim()) errors.office = "사무소명을 입력해주세요";
  if (!form.region.trim()) errors.region = "사무소 소재지를 입력해주세요";
  return errors;
};

const submitConsult = async (form) => {
  const fd = new FormData();
  fd.append("name", form.name.trim());
  fd.append("phone", (form.phone.match(/\d/g) || []).join(""));
  fd.append("tier", form.plan);
  fd.append("office", form.office.trim());
  fd.append("region", form.region.trim());
  fd.append("message", form.message.trim());
  const res = await fetch("/detail/postMembershipConsult/", {
    method: "POST",
    body: fd,
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error("network");
};

const Field = ({ label, required, error, children }) => (
  <div>
    <label>
      {label}
      {required && <span className="req">*</span>}
    </label>
    {children}
    {error && <div className="error-text">{error}</div>}
  </div>
);

const ConsultHead = () => (
  <div className="consult-head">
    <h2 style={{ fontSize: 24, marginBottom: 10, lineHeight: 1.48 }}>
      사무소 상황을 가장 잘 아는
      <br />
      담당 매니저가 맞춤 플랜을 제안합니다
    </h2>
    <p>가입 강요 없이, 편하게 문의하세요.</p>
    <div className="consult-contact">
      <div className="consult-contact-row">
        <div className="ic">
          <ClockIcon />
        </div>
        <div>
          <div className="t">상담 가능 시간</div>
          <div className="v">평일 오전 10시 – 오후 6시</div>
        </div>
      </div>
    </div>
  </div>
);

const ConsultSuccess = () => (
  <div className="form-success">
    <div className="ic">
      <CheckIcon size={32} />
    </div>
    <h3>상담 예약이 접수되었습니다</h3>
  </div>
);

const ConsultForm = ({ formRef }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: null }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitConsult(form);
      setSubmitted(true);
    } catch {
      setSubmitError("상담 신청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (k) => `input${errors[k] ? " error" : ""}`;

  return (
    <section className="section section-gray" id="consult" ref={formRef}>
      <div className="container">
        <div className="section-title-row">
          <h2>
            <span className="num">04</span> 상담 예약
          </h2>
        </div>
        <div className="consult">
          <ConsultHead />
          <div className="form-card">
            {submitted ? (
              <ConsultSuccess />
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="form-row two">
                  <Field label="이름" required error={errors.name}>
                    <input
                      className={inputClass("name")}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="홍길동"
                    />
                  </Field>
                  <Field label="연락처" required error={errors.phone}>
                    <input
                      className={inputClass("phone")}
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="010-0000-0000"
                    />
                  </Field>
                </div>
                <div className="form-row two">
                  <Field label="사무소명" required error={errors.office}>
                    <input
                      className={inputClass("office")}
                      value={form.office}
                      onChange={update("office")}
                      placeholder="○○공인중개사사무소"
                    />
                  </Field>
                  <Field label="사무소 소재지" required error={errors.region}>
                    <input
                      className={inputClass("region")}
                      value={form.region}
                      onChange={update("region")}
                      placeholder="경기도 분당구 판교동"
                    />
                  </Field>
                </div>
                <div className="form-row">
                  <label>관심 등급</label>
                  <select className="select" value={form.plan} onChange={update("plan")}>
                    {PLAN_OPTIONS.map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>문의 내용</label>
                  <textarea
                    className="textarea"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="궁금하신 점을 편하게 적어주세요."
                  />
                </div>
                {submitError && (
                  <div className="error-text" style={{ marginBottom: 8 }}>
                    {submitError}
                  </div>
                )}
                <button type="submit" className="form-submit" disabled={submitting}>
                  {submitting ? "신청 중..." : "상담 예약하기"}
                  {!submitting && <ArrowRightIcon size={18} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultForm;
